import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

const traduzirErroFirebase = (codigo) => {
  const mensagens = {
    'auth/user-not-found': 'Usuário não encontrado',
    'auth/wrong-password': 'Senha incorreta',
    'auth/email-already-in-use': 'Este e-mail já está em uso',
    'auth/invalid-credential': 'E-mail ou senha incorretos',
    'auth/invalid-email': 'E-mail inválido',
    'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres',
  };
  return mensagens[codigo] || 'Ocorreu um erro. Tente novamente.';
};

const obterIniciais = (nome) => {
  if (!nome) return '';
  const partes = nome.trim().split(/\s+/);
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        let dadosExtras = {};
        try {
          const refUsuario = doc(db, 'usuarios', firebaseUser.uid);
          const snap = await getDoc(refUsuario);
          if (snap.exists()) {
            dadosExtras = snap.data();
          }
        } catch (erro) {
          console.error('Erro ao buscar dados do usuário:', erro);
        }

        setUsuario({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          nome: dadosExtras.nome || firebaseUser.displayName || '',
          cargo: dadosExtras.cargo || '',
          avatar: dadosExtras.avatar || obterIniciais(firebaseUser.displayName || ''),
          ativo: dadosExtras.ativo ?? true,
          ...dadosExtras,
        });
      } else {
        setUsuario(null);
      }
      setCarregando(false);
    });

    return unsubscribe;
  }, []);

  const cadastrar = async (nome, email, senha) => {
    try {
      const credenciais = await createUserWithEmailAndPassword(auth, email, senha);
      await updateProfile(credenciais.user, { displayName: nome });

      await setDoc(doc(db, 'usuarios', credenciais.user.uid), {
        nome,
        email,
        cargo: 'Membro',
        avatar: obterIniciais(nome),
        criadoEm: serverTimestamp(),
        ativo: true,
      });

      return { sucesso: true };
    } catch (erro) {
      return { sucesso: false, mensagem: traduzirErroFirebase(erro.code) };
    }
  };

  const entrar = async (email, senha) => {
    try {
      await signInWithEmailAndPassword(auth, email, senha);
      return { sucesso: true };
    } catch (erro) {
      return { sucesso: false, mensagem: traduzirErroFirebase(erro.code) };
    }
  };

  const sair = () => signOut(auth);

  const valor = { usuario, carregando, cadastrar, entrar, sair };

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}
